import { ExpandableProductCard } from 'components/products/journey';
import { isEqual, omit } from 'lodash';
import querystring from 'querystring';
import { useCallback, useContext, useEffect, useRef } from 'react';
import { useHistory } from 'react-router';
import { useVirtual } from 'react-virtual';
import type { JourneyOrdersPath } from 'store/services/types';
import { useParseURLQueryParams } from 'utils';
import { JourneyContext } from './JourneyContext';

interface ThirdOrderContainerProps {
  data: JourneyOrdersPath[] | undefined;
}

export const ThirdOrderContainer = ({ data }: ThirdOrderContainerProps) => {
  const { query } = useParseURLQueryParams();
  const history = useHistory();

  const { thirdProduct, setThirdProduct, secondProduct } =
    useContext(JourneyContext);

  interface HandleSelect {
    product: JourneyOrdersPath;
    cardOpen: boolean;
  }
  const handleSelect = ({ product, cardOpen }: HandleSelect): void => {
    if (isEqual(product, thirdProduct) && cardOpen) {
      setThirdProduct(undefined);
      history.push({
        search: querystring.stringify(
          omit(
            {
              ...query,
            },
            'third_product_id'
          )
        ),
      });
      return;
    }

    setThirdProduct(product);
    history.push({
      search: querystring.stringify({
        ...query,
        third_product_id: product.id,
      }),
    });
  };

  useEffect(() => {
    if (query.third_product_id) {
      const selectedProduct = data?.find(
        (item) => String(item.id) === query.third_product_id
      );
      setThirdProduct(selectedProduct);
      return;
    }
    if (!!!query.third_product_id) {
      setThirdProduct(undefined);
    }
  }, [data, query.third_product_id, setThirdProduct]);

  const parentRef = useRef();
  const rowVirtualizer = useVirtual({
    size: data?.length || 0,
    parentRef,
    estimateSize: useCallback(
      (index) => {
        if (!!!thirdProduct?.id) return 100;
        if (data && data[index]?.id === thirdProduct?.id) {
          return 185;
        } else {
          return 100;
        }
      },
      // eslint-disable-next-line react-hooks/exhaustive-deps
      [thirdProduct?.id]
    ),
  });

  return (
    <div className="pr-1">
      {data?.length ? (
        <div
          //@ts-ignore
          ref={parentRef}
          className="pr-2 scrollbar-thin scrollbar-thumb-segments-darker-gray-75 scrollbar-thumb-rounded-lg scrollable-parent"
          style={{
            height: `780px`,
            width: `100%`,
            overflow: 'auto',
          }}
        >
          <div
            className="divide-y"
            style={{
              height: `${rowVirtualizer.totalSize}px`,
              width: '100%',
              position: 'relative',
            }}
          >
            {rowVirtualizer.virtualItems.map((virtualRow) => (
              <div
                className="py-2 virtualized-child"
                key={virtualRow.index}
                style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  width: '100%',
                  height: `${virtualRow.size}px`,
                  transform: `translateY(${virtualRow.start}px)`,
                }}
              >
                <ExpandableProductCard
                  data={data && data[virtualRow.index]}
                  key={data && data[virtualRow.index].id}
                  selected={thirdProduct}
                  setSelected={handleSelect}
                  hoverActionIndicator={false}
                />
              </div>
            ))}
          </div>
        </div>
      ) : secondProduct ? (
        <div className="grid h-full text-black place-items-center text-opacity-30">
          <p>No third order found on this product journey.</p>
        </div>
      ) : (
        <div className="grid h-full text-black place-items-center text-opacity-30">
          <p>Select 2nd product to analyze.</p>
        </div>
      )}
    </div>
  );
};
